import { Box, Button, Card, CardContent, Chip, Paper, Stack, TextField, Typography, Divider, CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import { Classroom } from "../models/classroom";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import renderAvatar from "../components/render-avatar";
import { newStudent, Student } from "../models/student";
import { useSnackbar } from "../components/snackbar";
import { useStudentActions } from "../hooks/useStudentActions";
import { RenderRichText } from "../components/render-rich-text";
import BackdropLoader from "../components/backdrop-loader";

// display student name later
const API_URL = process.env.REACT_APP_API_URL;

export default function Enroll() {
    const { enrollment_code } = useParams<{ enrollment_code: string }>();

    const navigate = useNavigate();

    const [classroom, setClassroom] = useState<Classroom | null>();
    

    const [fetchingUrl, setFetchingUrl] = useState(false);
    const [url, setUrl] = useState(null);

    const { student } = useStudentActions();

    const [locStudent, setLocStudent] = useState<Student | null>(null);

    const { showMessage } = useSnackbar();
    

    const handleClassroomChange = (field: keyof Classroom, value: any) => {
        setClassroom((prev) => {
            if (!prev) {
                return prev;
            }
            return {
                ...prev,
                [field]: value
            };
        });
    };

    const handleSubmit = async () => {
        try {
            setFetchingUrl(true);
            const response = await axios.get(
                `${API_URL}/utils/students/payment-url?classroom_id=${classroom?.id}`,
                { withCredentials: true }
            );
            setUrl(response.data); 
            window.open(response.data, "_blank");
        } catch (error) {
            handleApiError(error);
        } finally {
            setFetchingUrl(false);
        }
        
        
    };

    const calculateStudentCharges = (): number => {
        const ins_amm = Number(classroom?.instructor_amount) || 0;
        return +(Math.max(100 + ins_amm, ins_amm + (ins_amm * 0.05))).toFixed(2);
    };

    const handleApiError = (error: any) => {
        if (error?.response?.status === 401) {
            navigate("/sign-in");
            return;
        }

        const message =
            error?.response?.data?.detail ||
            error?.response?.data?.message ||
            error.message ||
            "An unexpected error occurred";

        showMessage(message, 0);
    };

    useEffect(() => {
        const getClassDetails = async () => {
            try {
                const response = await axios.get<Classroom>(
                    `${API_URL}/hub/classrooms/${enrollment_code!}/preview`,
                    { withCredentials: true }
                );
                setClassroom(response.data);
            } catch (error) {
                handleApiError(error);
            }
        };
        getClassDetails();
    }, [enrollment_code]);

    useEffect(() => {
        const stored = localStorage.getItem("student");

        if (stored) {
            const parsed = JSON.parse(stored);
            setLocStudent(parsed);
        }
    }, [student]);
  
    if (!classroom) {
        return(<BackdropLoader/>);
    }

    
    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "#c6e1f5", py: 4 }}>
            <Box maxWidth="800px" mx="auto" paddingX={1}>
                <Card sx={{ p: 3, borderRadius: 2 }}>
                    <Stack spacing={3}>
                        {/* Header Info */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                        {renderAvatar(classroom.id, classroom.name, classroom.image, 80, 80)}
                        <Box>
                            <Typography variant="h5" fontWeight={700}>
                            {classroom.name}
                            </Typography>
                            <Typography variant="subtitle1" color="text.secondary">
                            {classroom.hub_name}
                            </Typography>
                        </Box>
                        </Box>

                        {/* Enrollment Fee & Type */}
                        <Box
                        sx={{
                            bgcolor: "grey.50",
                            borderRadius: 1,
                            p: 2,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                        }}
                        >
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="body1" fontWeight={500}>
                            Enrollment Fee <Typography component="span" variant="caption">(includes platform charges)</Typography>
                            </Typography>
                            <Typography variant="h6" color="primary">
                            ₦{calculateStudentCharges()}
                            </Typography>
                        </Box>

                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography variant="body2" fontWeight={500}>
                            Enrollment Type
                            </Typography>
                            <Chip
                            label={
                                classroom.enrollment_type === 0
                                ? "One Time"
                                : `${classroom.duration} days`
                            }
                            color={classroom.enrollment_type === 0 ? "success" : "info"}
                            size="small"
                            variant="outlined"
                            />
                        </Box>
                        </Box>

                        {/* Description */}
                        <Box>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                            Notice Board
                        </Typography>
                        {/* Light divider line */}
                            <Box
                                sx={{
                                borderBottom: "1px solid",
                                borderColor: "divider",
                                mb: 2,
                                }}
                            />
                            <RenderRichText jsonContent={classroom.description} />
                        </Box>
                    </Stack>
                </Card>

                <Card sx={{ mt: 4, p: 1, borderRadius: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom fontWeight={600}>
                            Student Information
                        </Typography>

                        <Stack spacing={3} mt={2}>
                            {locStudent && (
                                <TextField
                                    value={locStudent.name}
                                    disabled
                                    fullWidth
                                />
                            )}
                            <TextField
                                label="Reg. Number (Optional)"
                                value={classroom.reg_number || ""}
                                disabled={!locStudent}
                                onChange={(e) =>
                                    handleClassroomChange("reg_number", e.target.value)
                                }
                                fullWidth
                            />

                            {locStudent ? (
                                <>
                                    <Button
                                        onClick={handleSubmit}
                                        variant="contained"
                                        fullWidth
                                        size="large"
                                        sx={{
                                            textTransform: "none",
                                            fontSize: "1.1rem",
                                            fontWeight: 600,
                                            py: 1.5,
                                            borderRadius: 2,
                                            background: "linear-gradient(135deg, #667eea, #764ba2)",
                                            "&:hover": {
                                                background: "linear-gradient(135deg, #5a67d8, #6b46c1)",
                                            },
                                        }}
                                    >
                                        {fetchingUrl ? <CircularProgress size={16} color="inherit" /> : "Enroll in Classroom"}
                                    </Button>
                                
                                   {url && (
                                        <Box 
                                            sx={{ 
                                                bgcolor: "grey.50", 
                                                borderRadius: 1, 
                                                p: 1, 
                                                display: 'flex',
                                                justifyContent: 'center', 
                                                alignItems: 'center' 
                                            }}
                                        >
                                            <a
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: "#667eea", fontWeight: 600, wordBreak: "break-all", textAlign: 'center' }}
                                            >
                                                {url}
                                            </a>
                                        </Box>
                                    )}
                                </>

                            ) : (
                                <Button
                                    onClick={() => navigate("/sign-in")}
                                    variant="outlined"
                                    color="primary"
                                    fullWidth
                                    size="large"
                                    sx={{
                                        textTransform: "none",
                                        fontSize: "1.1rem",
                                        fontWeight: 600,
                                        py: 1.5,
                                        borderRadius: 2,
                                    }}
                                >
                                    Sign in to Enroll
                                </Button>
                            )}

                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                Already enrolled?{" "}
                                <a
                                    href="https://students.elms.ng"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: "#667eea", fontWeight: 600 }}
                                >
                                    Visit students.elms.ng
                                </a>
                            </Typography>

                        </Stack>
                        
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );

}